import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ─── Theme ─── */
const h = {
  cream: "#FAF7F2", white: "#FFFFFF", taupe: "#C4A882", taupeL: "#E8DDD0",
  taupeD: "#A68B6B", gold: "#C4A055", goldL: "#F5ECD7", text: "#2D2A26",
  muted: "#7A7067", light: "#9E9589", accent: "#8B7355", border: "#E8E2DA",
  ok: "#7A9E7E", okL: "#EDF5EE", btnPrimary: "#8B6F4E"
};
const O = { fontFamily: "system-ui,-apple-system,'Segoe UI',sans-serif" };

/* ─── Sections Data ─── */
const SECTIONS = [
  {
    id: 1, title: "Mindset First", sub: "Clear the blocks before you build", icon: "\u2661",
    lessons: [
      { id: "4a", type: "learn", title: "Fear Is Normal. Do It Anyway.",
        content: [
          "Every successful entrepreneur you admire has felt exactly what you\u2019re feeling right now. The fear of failure, the imposter syndrome, that voice that whispers \u2018who am I to do this?\u2019",
          "The difference? They felt the fear and took action anyway. Not because they were braver, but because they understood that fear means something matters to you. It\u2019s information, not a stop sign.",
          "At 50+, you\u2019ve already survived things far harder than starting a business. You\u2019ve raised families, navigated careers, weathered losses, rebuilt yourself. You are already resilient. You\u2019ve already proven you can handle hard things. This is just the next chapter.",
          "And about self-care: this isn\u2019t optional fluff. Burnout kills more businesses than bad strategy. Set boundaries with your time. Take care of your body. Read books. Stay curious. The business you build should enhance your life, not consume it."
        ],
        takeaway: "You don\u2019t need to eliminate fear. You need to stop letting it make your decisions. And take care of yourself along the way."
      },
      { id: "4b", type: "activity", title: "Rewrite Your Limiting Beliefs",
        intro: "Let\u2019s tackle those inner voices head-on. Name the fear, then replace it with what\u2019s actually true.",
        fields: [
          { id: "f1", label: "A fear or belief holding you back:", ph: "e.g., I'm too old to learn new technology" },
          { id: "t1", label: "The truth that replaces it:", ph: "e.g., I've learned countless new things. Technology is just the next one." },
          { id: "f2", label: "Another fear or belief:", ph: "e.g., Nobody will pay for what I know" },
          { id: "t2", label: "The truth:", ph: "e.g., My 30+ years of experience have given me insights that are incredibly valuable." }
        ]
      },
      { id: "4c", type: "reflect", title: "Your Commitment",
        prompt: "Now that you\u2019ve faced those fears, write yourself a commitment. What will you do differently starting today? What mindset shift are you making right now?",
        fieldId: "r_commit"
      }
    ]
  },
  {
    id: 2, title: "Your Unique Journey", sub: "Your experience is your superpower", icon: "\u2726",
    lessons: [
      { id: "1a", type: "learn", title: "Your Experience Matters",
        content: [
          "Every trial and triumph you\u2019ve faced has shaped you into the incredible woman you are today. Your decades of experience aren\u2019t just memories. They\u2019re your competitive advantage in business.",
          "You have something that can\u2019t be fast-tracked: wisdom, resilience, and a deep understanding of people. Those qualities build businesses that last.",
          "In 2026, AI tools have leveled the playing field on the technical side. You no longer need to be a tech genius to build a website, write marketing copy, or automate your business. What you DO need is the strategic thinking and life wisdom to use these tools effectively. And that\u2019s exactly what you have."
        ],
        takeaway: "Your life experience is not a limitation. Combined with today\u2019s AI tools, it\u2019s an unstoppable foundation for business success."
      },
      { id: "1b", type: "activity", title: "Map Your Milestone Timeline",
        intro: "Let\u2019s turn your life experience into business assets. Think about 3 key moments that shaped who you are.",
        fields: [
          { id: "m1_event", label: "First milestone (year & event)", ph: "e.g., 1995 \u2014 Started managing a team of 20" },
          { id: "m1_skill", label: "What skill or insight did this give you?", ph: "e.g., Leadership, conflict resolution, reading people" },
          { id: "m2_event", label: "Second milestone", ph: "e.g., 2005 \u2014 Navigated a major career change" },
          { id: "m2_skill", label: "Skill or insight gained?", ph: "e.g., Adaptability, courage under pressure" },
          { id: "m3_event", label: "Third milestone", ph: "e.g., 2018 \u2014 Helped a friend launch her business" },
          { id: "m3_skill", label: "Skill or insight gained?", ph: "e.g., Strategic thinking, seeing the big picture" }
        ]
      },
      { id: "1c", type: "reflect", title: "Connect the Dots",
        prompt: "Look at the skills you just identified. How could these specific strengths serve your ideal client? What makes YOUR perspective uniquely valuable in the market?",
        fieldId: "r_journey"
      }
    ]
  },
  {
    id: 3, title: "Vision, Plan & AI Tools", sub: "Clarity creates confidence", icon: "\u25C6",
    lessons: [
      { id: "2a", type: "learn", title: "Your Vision + A Smarter Plan",
        content: [
          "A clear vision isn\u2019t just motivational. It\u2019s the filter for every decision you\u2019ll make. Should I offer this service? Does it align with my vision? Should I partner with this person? Does it move me toward my vision?",
          "The most successful entrepreneurs over 50 aren\u2019t trying to build the next tech empire. They\u2019re building businesses that align with their values, serve people they care about, and give them the lifestyle they actually want.",
          "Here\u2019s what\u2019s changed in 2026: you don\u2019t need a 50-page business plan anymore. AI tools like Claude, ChatGPT, and others can help you do market research in minutes, draft your business plan, analyze competitors, and even project revenue. The strategic thinking is still yours. The grunt work? AI handles that now.",
          "Your job is to know WHAT you want. The tools will help you figure out HOW to get there faster than ever before."
        ],
        takeaway: "Combine your hard-won clarity about what matters with AI tools that handle the heavy lifting. That\u2019s the 2026 advantage."
      },
      { id: "2b", type: "activity", title: "Craft Your Vision Statement",
        intro: "Answer each question honestly. Don\u2019t overthink it. Your first instinct is usually right.",
        fields: [
          { id: "v_goals", label: "What are your top 3 business goals?", ph: "e.g., Financial independence, helping others grow, flexible schedule" },
          { id: "v_impact", label: "What impact do you want to make?", ph: "e.g., Help women feel confident navigating technology in business" },
          { id: "v_who", label: "Who specifically do you want to serve?", ph: "e.g., Women over 50 starting their first online business" },
          { id: "v_draft", label: "Now combine these into 1\u20132 sentences:", ph: "e.g., I empower women over 50 to build thriving businesses by making strategy approachable and tech feel doable." }
        ]
      },
      { id: "2c", type: "activity", title: "Your 90-Day Quick Plan",
        intro: "Forget the massive business plan. What will you accomplish in the next 90 days? Be specific but realistic.",
        fields: [
          { id: "g30", label: "By Day 30, I will have...", ph: "e.g., Defined my core offer and created a simple one-page website" },
          { id: "g60", label: "By Day 60, I will have...", ph: "e.g., Started showing up on Facebook consistently and connected with 10 potential clients" },
          { id: "g90", label: "By Day 90, I will have...", ph: "e.g., Signed my first 3 paying clients and set up email marketing" }
        ]
      }
    ]
  },
  {
    id: 4, title: "Your Ideal Client & Presence", sub: "Attract exactly the right people", icon: "\u25CF",
    lessons: [
      { id: "3a", type: "learn", title: "Know Exactly Who You Serve",
        content: [
          "Here\u2019s a truth that transforms businesses: you can\u2019t serve everyone well. And when you try to, your message gets watered down until nobody feels like you\u2019re talking to them.",
          "The more specifically you define your ideal client, the easier everything becomes. Your messaging gets clearer, your offers get stronger, and the right people start finding you. It feels counterintuitive, but narrowing your focus actually grows your business.",
          "In 2026, AI can help you research your audience, analyze what content resonates, and even create customer personas. But the foundation is still the same: deeply understanding the one person you\u2019re built to serve. Once you know her, everything else falls into place."
        ],
        takeaway: "Specificity is your friend. A narrow focus doesn\u2019t limit your business. It sharpens everything you do."
      },
      { id: "3b", type: "activity", title: "Build Your Client Avatar",
        intro: "Create a vivid picture of the ONE person your business exists to help. Give her a name. Make her real.",
        fields: [
          { id: "av_name", label: "Give her a name", ph: "e.g., Sandra" },
          { id: "av_age", label: "How old is she?", ph: "e.g., 54" },
          { id: "av_sitch", label: "What\u2019s her current situation?", ph: "e.g., Recently retired teacher wanting to start coaching" },
          { id: "av_pain", label: "Her biggest struggle right now?", ph: "e.g., Overwhelmed by technology, doesn't know where to start" },
          { id: "av_dream", label: "What does she dream about?", ph: "e.g., A thriving online business that gives her purpose and income" },
          { id: "av_stop", label: "What\u2019s stopping her from getting help?", ph: "e.g., Thinks she's too old, worried about wasting money" }
        ]
      },
      { id: "3c", type: "learn", title: "Your Online Presence",
        content: [
          "The digital landscape has changed massively. AI helps you create content, optimize your website, and engage your audience in ways that used to need a full team. A single person with the right tools can now compete with businesses that have 10 employees.",
          "But here\u2019s what hasn\u2019t changed: people buy from people they trust. Your online presence should feel like YOU. Authentic, knowledgeable, approachable. Not polished to perfection, but real and relatable.",
          "Start with ONE platform where your ideal client hangs out. Master that before adding anything else. Show up consistently with valuable content 3-4 times per week. Engage in conversations. Build real relationships. That\u2019s the strategy. It\u2019s not complicated. It just requires consistency."
        ],
        takeaway: "Pick ONE platform. Show up consistently. Be yourself. That\u2019s the whole strategy."
      }
    ]
  }
];

const STAGES = [
  { id: "idea", label: "Just an idea", desc: "I'm thinking about starting but haven't taken action yet" },
  { id: "starting", label: "Getting started", desc: "I've started but I'm still figuring things out" },
  { id: "growing", label: "I have a business", desc: "I'm running a business and want to grow or improve it" },
  { id: "restart", label: "Starting over", desc: "I've done this before and I'm pivoting or rebuilding" }
];

const AFFIRMATIONS = {
  idea: ["Every expert was once a beginner. Today I take my first step.", "My idea has value. It's time to explore it.", "I don't need to have it all figured out to get started.", "The perfect time to start is now. Not tomorrow. Now."],
  starting: ["Progress, not perfection. Every small step counts.", "I'm figuring this out, and that's exactly how it's supposed to work.", "Confusion is part of the process. Clarity comes from action.", "I am building something meaningful, one day at a time."],
  growing: ["My business is proof that I can do hard things.", "Growth requires letting go of what got me here to reach what's next.", "I don't need to do more. I need to do what matters.", "The next level of my business starts with the next level of me."],
  restart: ["Starting over isn't starting from scratch. I bring everything I've learned.", "My experience from before is my unfair advantage now.", "This time, I know what NOT to do. That's powerful.", "Reinvention is strength, not failure."],
  default: ["Done is better than perfect. Progress beats perfection every single time.", "I am exactly the right person to serve my ideal clients.", "Technology is a tool, not a barrier. I can learn anything I decide to learn.", "My voice matters. My experience matters. My business matters.", "I choose courage over comfort, action over overthinking."]
};

const MAX_COACH_PER_DAY = 50;

function getCoachLimit() {
  try {
    const d = JSON.parse(localStorage.getItem("be-coach-limit") || "{}");
    const today = new Date().toISOString().slice(0, 10);
    return d.date !== today ? { count: 0, date: today } : { count: d.count || 0, date: today };
  } catch { return { count: 0, date: new Date().toISOString().slice(0, 10) }; }
}

function bumpCoachLimit() {
  const d = getCoachLimit();
  d.count++;
  try { localStorage.setItem("be-coach-limit", JSON.stringify(d)); } catch {}
  return d.count;
}

/* ─── Small Components ─── */
const Wrapper = ({ children, style }) => (
  <div style={{ maxWidth: 640, margin: "0 auto", ...style }}>{children}</div>
);

const Btn = ({ children, onClick, onMouseDown, sx, v, ...rest }) => {
  const small = v === "s";
  return (
    <button
      data-testid={rest["data-testid"] || "btn"}
      onClick={onClick}
      onMouseDown={onMouseDown}
      style={{
        background: h.btnPrimary, color: "#fff", border: "none",
        borderRadius: 8, padding: small ? "8px 16px" : "14px 28px",
        fontSize: small ? 13 : 16, fontWeight: 500, cursor: "pointer",
        ...O, transition: "opacity .15s", ...sx
      }}
    >
      {children}
    </button>
  );
};

const BackBtn = ({ onClick, label }) => (
  <button
    data-testid="back-btn"
    onClick={onClick}
    style={{ background: "none", border: "none", cursor: "pointer", color: h.muted, fontSize: 13, ...O, padding: 0, marginBottom: 20, display: "flex", alignItems: "center", gap: 6 }}
  >
    {"<"} {label || "Back"}
  </button>
);

const ProgressBar = ({ pct, height = 3 }) => (
  <div style={{ width: "100%", background: h.border, borderRadius: height, height, overflow: "hidden" }}>
    <div style={{ background: `linear-gradient(90deg,${h.btnPrimary},${h.gold})`, height: "100%", width: `${pct}%`, borderRadius: height, transition: "width .5s" }} />
  </div>
);

/* ─── TextArea Component (CONTROLLED - fixes the erase bug) ─── */
const TextArea = ({ id, label, ph, val, onChange, rows = 3 }) => (
  <div style={{ marginBottom: 20 }} data-testid={`field-${id}`}>
    <label style={{ display: "block", fontSize: 14, ...O, fontWeight: 500, color: h.text, marginBottom: 6 }}>{label}</label>
    <textarea
      data-testid={`textarea-${id}`}
      value={val || ""}
      onChange={e => onChange(id, e.target.value)}
      placeholder={ph}
      rows={rows}
      style={{
        width: "100%", padding: "13px 16px", borderRadius: 8,
        border: `1.5px solid ${h.border}`, background: h.white,
        fontSize: 15, ...O, color: h.text, outline: "none",
        resize: "vertical", lineHeight: 1.55, boxSizing: "border-box"
      }}
    />
  </div>
);

/* ─── Main App ─── */
function App() {
  const [ready, setReady] = useState(false);
  const [view, setView] = useState("welcome");
  const [secIdx, setSecIdx] = useState(0);
  const [lesIdx, setLesIdx] = useState(0);
  const [ans, setAns] = useState({});
  const [done, setDone] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState("");
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const [chatMsgs, setChatMsgs] = useState([]);
  const chatEndRef = useRef(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [emailErr, setEmailErr] = useState("");
  const [warning, setWarning] = useState("");
  const [personalized, setPersonalized] = useState({});
  const [personalizing, setPersonalizing] = useState(false);
  const [genPlan, setGenPlan] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [coachCount, setCoachCount] = useState(getCoachLimit().count);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [coachInput, setCoachInput] = useState("");
  const scrollRef = useRef(null);
  const chatScrollRef = useRef(null);

  const affs = AFFIRMATIONS[stage] || AFFIRMATIONS.default;
  const [affIdx] = useState(() => {
    const d = new Date().toISOString().slice(0, 10);
    let v = 0;
    for (let i = 0; i < d.length; i++) v = (v << 5) - v + d.charCodeAt(i) | 0;
    return Math.abs(v);
  });
  const affirmation = affs[affIdx % affs.length];

  // Responsive
  useEffect(() => {
    const fn = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  // Load from localStorage OR URL params (for Facebook Lead Form ads)
  useEffect(() => {
    // Check for URL params OR hash params (Facebook lead form redirect)
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace("#", "?"));
    const urlName = params.get("name") || params.get("first_name") || hashParams.get("name") || hashParams.get("first_name") || "";
    const urlEmail = params.get("email") || hashParams.get("email") || "";

    if (urlName && urlEmail) {
      // Coming from Facebook lead form - skip welcome
      setName(urlName);
      setEmail(urlEmail);
      // Clean the URL
      window.history.replaceState({}, "", window.location.pathname);
      // Send to Kartra
      fetch(`${API}/kartra`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ first_name: urlName, email: urlEmail }) }).catch(() => {});
      // Check if they have existing progress
      try {
        const d = localStorage.getItem("be-data");
        if (d) {
          const p = JSON.parse(d);
          if (p.email === urlEmail && p.stage) {
            setStage(p.stage);
            setAns(p.ans || {});
            setDone(p.done || []);
            if (p.genPlan) setGenPlan(p.genPlan);
            setView("dashboard");
            setReady(true);
            return;
          }
        }
      } catch {}
      setView("stage");
      setReady(true);
      return;
    }

    // Normal load from localStorage
    try {
      const d = localStorage.getItem("be-data");
      if (d) {
        const p = JSON.parse(d);
        if (p.name) {
          setName(p.name);
          setEmail(p.email || "");
          setStage(p.stage || "");
          setAns(p.ans || {});
          setDone(p.done || []);
          if (p.genPlan) setGenPlan(p.genPlan);
          setView("dashboard");
        }
      }
    } catch {}
    setReady(true);
  }, []);

  // Auto-save to localStorage
  const saveData = useCallback((n, e, a, d, s, g) => {
    try {
      localStorage.setItem("be-data", JSON.stringify({ name: n, email: e, ans: a, done: d, stage: s, genPlan: g }));
    } catch {}
  }, []);

  useEffect(() => {
    if (ready && name) saveData(name, email, ans, done, stage, genPlan);
  }, [ans, done, name, email, stage, genPlan, ready, saveData]);

  // Scroll to top on view change
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [view, secIdx, lesIdx]);

  // Auto-generate strategy when arriving at summary with all steps done
  useEffect(() => {
    if (view === "summary" && allFilled && !genPlan && !genLoading) {
      generateStrategy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Scroll chat
  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatMsgs, chatLoading]);

  // AI personalization for learn lessons
  useEffect(() => {
    if (view === "lesson") {
      const les = SECTIONS[secIdx].lessons[lesIdx];
      if (les.type === "learn" && !personalized[les.id]) {
        const ctx = buildContext();
        if (ctx.length >= 40) {
          setPersonalizing(true);
          fetch(`${API}/chat`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              messages: [{ role: "user", content: `Rewrite this lesson to be personally relevant to this user. Reference their specific details naturally — use their name, their fears, their goals, their client details. Keep the same teaching points but make it feel like you're talking directly to them. 2-3 short paragraphs max. End with KEY TAKEAWAY: and a personalized one-liner. No bullets. No emoji. Do NOT use markdown formatting.\n\nORIGINAL LESSON:\n${les.content.join("\n\n")}\n\nKEY TAKEAWAY: ${les.takeaway}\n\nUSER CONTEXT:\n${ctx}` }],
              context: ctx
            })
          }).then(r => r.json()).then(data => {
            if (data.reply) {
              try {
                const parts = data.reply.split("KEY TAKEAWAY:");
                setPersonalized(prev => ({
                  ...prev,
                  [les.id]: {
                    content: parts[0].split("\n\n").filter(p => p.trim()),
                    takeaway: parts[1] ? parts[1].trim() : les.takeaway
                  }
                }));
              } catch {}
            }
            setPersonalizing(false);
          }).catch(() => setPersonalizing(false));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, secIdx, lesIdx]);

  const padding = isDesktop ? 48 : 24;

  /* ─── Helpers ─── */
  const totalLessons = SECTIONS.reduce((a, s) => a + s.lessons.length, 0);
  const progressPct = Math.round(done.length / totalLessons * 100);

  const secProgress = (idx) => {
    const s = SECTIONS[idx];
    return { d: s.lessons.filter(l => done.includes(l.id)).length, t: s.lessons.length };
  };

  const updateAns = (key, val) => setAns(prev => ({ ...prev, [key]: val }));
  const markDone = (id) => { if (!done.includes(id)) setDone(prev => [...prev, id]); };
  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const allFieldIds = SECTIONS.flatMap(s => s.lessons.flatMap(l =>
    l.type === "activity" ? l.fields.map(f => f.id) : l.type === "reflect" ? [l.fieldId] : []
  ));
  const filledFields = allFieldIds.filter(id => (ans[id] || "").trim().length > 0);
  const allFilled = filledFields.length >= allFieldIds.length;

  const buildContext = () => {
    let ctx = "";
    if (name && name !== "Friend") ctx += `Name: ${name}. `;
    if (stage) ctx += `Stage: ${(STAGES.find(s => s.id === stage) || {}).label || stage}. `;
    if (ans.f1) ctx += `Fear: ${ans.f1} -> Truth: ${ans.t1 || ""}. `;
    if (ans.f2) ctx += `Fear: ${ans.f2} -> Truth: ${ans.t2 || ""}. `;
    if (ans.r_commit) ctx += `Commitment: ${ans.r_commit}. `;
    if (ans.m1_event) ctx += `Milestones: ${[ans.m1_event, ans.m2_event, ans.m3_event].filter(Boolean).join(", ")}. `;
    if (ans.m1_skill) ctx += `Skills: ${[ans.m1_skill, ans.m2_skill, ans.m3_skill].filter(Boolean).join(", ")}. `;
    if (ans.r_journey) ctx += `Strengths reflection: ${ans.r_journey}. `;
    if (ans.v_goals) ctx += `Goals: ${ans.v_goals}. `;
    if (ans.v_impact) ctx += `Impact: ${ans.v_impact}. `;
    if (ans.v_who) ctx += `Audience: ${ans.v_who}. `;
    if (ans.v_draft) ctx += `Vision: ${ans.v_draft}. `;
    if (ans.g30) ctx += `30-day: ${ans.g30}. `;
    if (ans.g60) ctx += `60-day: ${ans.g60}. `;
    if (ans.g90) ctx += `90-day: ${ans.g90}. `;
    if (ans.av_name) ctx += `Ideal client: ${ans.av_name}${ans.av_age ? ", age " + ans.av_age : ""}. `;
    if (ans.av_sitch) ctx += `Client situation: ${ans.av_sitch}. `;
    if (ans.av_pain) ctx += `Client struggle: ${ans.av_pain}. `;
    if (ans.av_dream) ctx += `Client dream: ${ans.av_dream}. `;
    if (ans.av_stop) ctx += `Client objection: ${ans.av_stop}. `;
    return ctx;
  };

  /* ─── Continue Handler ─── */
  const handleContinue = (e) => {
    if (e) e.preventDefault();
    const sec = SECTIONS[secIdx];
    const les = sec.lessons[lesIdx];

    if (les.type === "activity" && les.fields.filter(f => (ans[f.id] || "").trim().length > 0).length < Math.ceil(les.fields.length / 2)) {
      setWarning("Your answers here shape the rest of the guide and your personalized plan. Take a few minutes to fill them in.");
      return;
    }
    if (les.type === "reflect" && !(ans[les.fieldId] || "").trim()) {
      setWarning("Even a few sentences here will make your plan more meaningful. Give it a try.");
      return;
    }

    setWarning("");
    markDone(les.id);

    if (lesIdx < sec.lessons.length - 1) {
      setLesIdx(lesIdx + 1);
    } else if (secIdx < SECTIONS.length - 1) {
      setSecIdx(secIdx + 1);
      setLesIdx(0);
      setView("step");
    } else {
      // All steps done - go straight to summary and auto-generate
      setView("summary");
    }
  };

  /* ─── Welcome Submit ─── */
  const handleWelcome = () => {
    const n = nameRef.current?.value?.trim();
    const e = emailRef.current?.value?.trim();
    if (!e || !isValidEmail(e)) {
      setEmailErr("Please enter a valid email so we can save your progress.");
      return;
    }
    setName(n || "Friend");
    setEmail(e);
    setEmailErr("");
    // Kartra lead capture
    fetch(`${API}/kartra`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ first_name: n || "Friend", email: e }) }).catch(() => {});
    setView("stage");
  };

  /* ─── Stage Select ─── */
  const handleStage = (stg) => {
    setStage(stg);
    setView("dashboard");
  };

  /* ─── Strategy Generation ─── */
  const generateStrategy = async () => {
    const ctx = buildContext();
    if (ctx.length < 40) return;
    setGenLoading(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [{
            role: "user",
            content: `BUSINESS SNAPSHOT:\n${ctx}\n\nIMPORTANT: Base your ENTIRE response on the specific details above. If the answers seem vague, random, or don't make business sense, call that out honestly and ask them to revisit their answers. Do NOT generate generic advice.\n\nCreate a personalized action plan using these EXACT section headers:\nYOUR UNFAIR ADVANTAGE:\nRECOMMENDED FIRST OFFER:\nCLIENT ATTRACTION STRATEGY:\nBIGGEST RISK TO WATCH:\n\nIn each section, directly reference her specific goals, client details, fears, and skills by name. Keep each section 2-4 sentences. Be warm but direct. No fluff. No generic advice that could apply to anyone.`
          }],
          context: ctx,
          is_plan_gen: true
        })
      });
      const data = await res.json();
      if (data.reply) {
        const plan = {};
        const headers = ["YOUR UNFAIR ADVANTAGE", "RECOMMENDED FIRST OFFER", "CLIENT ATTRACTION STRATEGY", "BIGGEST RISK TO WATCH"];
        headers.forEach((hdr, i) => {
          // Try with colon first, then without
          let start = data.reply.indexOf(hdr + ":");
          if (start === -1) start = data.reply.indexOf(hdr);
          if (start !== -1) {
            const contentStart = start + hdr.length + (data.reply[start + hdr.length] === ":" ? 1 : 0);
            let end = data.reply.length;
            for (let j = i + 1; j < headers.length; j++) {
              let nextIdx = data.reply.indexOf(headers[j] + ":");
              if (nextIdx === -1) nextIdx = data.reply.indexOf(headers[j]);
              if (nextIdx !== -1 && nextIdx > contentStart) { end = nextIdx; break; }
            }
            plan[hdr] = data.reply.slice(contentStart, end).trim();
          }
        });
        // If no headers found, put the whole response as the unfair advantage
        if (Object.keys(plan).length === 0 && data.reply.trim()) {
          plan["YOUR UNFAIR ADVANTAGE"] = data.reply.trim();
        }
        setGenPlan(plan);
      }
    } catch {}
    setGenLoading(false);
  };

  /* ─── AI Coach Chat ─── */
  const sendChat = async (text) => {
    if (!text.trim() || chatLoading) return;
    if (coachCount >= MAX_COACH_PER_DAY) return;

    const newMsgs = [...chatMsgs, { role: "user", text }];
    setChatMsgs(newMsgs);
    setChatLoading(true);
    setCoachCount(bumpCoachLimit());

    try {
      const apiMsgs = newMsgs.map(m => ({
        role: m.role === "user" ? "user" : "assistant",
        content: m.text
      }));
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: apiMsgs, context: buildContext() })
      });
      const data = await res.json();
      setChatMsgs(prev => [...prev, { role: "coach", text: data.reply || "I'm having trouble right now. Try again." }]);
    } catch {
      setChatMsgs(prev => [...prev, { role: "coach", text: "I'm having trouble connecting. Try again or reach out at support@robynbennett.co." }]);
    }
    setChatLoading(false);
  };

  /* ─── Reset ─── */
  const resetAll = () => {
    setAns({});
    setDone([]);
    setChatMsgs([]);
    setStage("");
    setPersonalized({});
    setGenPlan(null);
    setGenLoading(false);
    setCoachInput("");
    setChatOpen(false);
    try { localStorage.setItem("be-data", JSON.stringify({ name, email, ans: {}, done: [], stage: "" })); } catch {}
    setView("stage");
  };

  if (!ready) return null;

  /* ─── VIEWS ─── */

  const WelcomeView = () => (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: isDesktop ? "60px 48px" : "20px 20px" }}>
      <div style={{ maxWidth: isDesktop ? 960 : 640, margin: "0 auto", width: "100%" }}>
        {isDesktop ? (
          /* Desktop: side by side */
          <div style={{ display: "flex", gap: 60, alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <img src="/logo.png" alt="Robyn Bennett" style={{ height: 60, marginBottom: 18, opacity: 0.9 }} />
              <h1 style={{ fontSize: 42, fontWeight: 400, lineHeight: 1.2, marginBottom: 14 }}>
                The Business<br />Essentials Guide
              </h1>
              <p style={{ fontSize: 15, color: h.taupe, ...O, fontWeight: 500, marginBottom: 16 }}>For women over 50 who are ready to turn their experience into income.</p>
              <p style={{ fontSize: 15.5, color: h.muted, lineHeight: 1.7, ...O, marginBottom: 20 }}>
                Answer real questions about YOUR life and goals, get on-demand guidance, and walk away with a business strategy built around you. Not a PDF you'll forget about.
              </p>
              <div style={{ marginBottom: 8 }}>
                {["Uncover the business strengths hiding in your experience", "Get a customized strategy mapping out your first offer and pricing", "A 90-day roadmap with specific action steps", "On-demand support that adapts to your exact situation"].map((t, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                    <span style={{ color: h.taupe, fontSize: 14, marginTop: 1 }}>&#10003;</span>
                    <span style={{ fontSize: 15, ...O, color: h.text, lineHeight: 1.55 }}>{t}</span>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 14, ...O, color: h.accent, marginTop: 20, fontStyle: "italic", lineHeight: 1.6 }}>
                "I wish someone had given me this when I started. Clear, simple, and it actually understands where I'm coming from."
              </p>
            </div>
            <div style={{ flex: "0 0 380px" }}>
              <div style={{ background: h.white, borderRadius: 16, padding: "36px 32px", border: `1px solid ${h.border}`, boxShadow: "0 4px 24px rgba(0,0,0,.04)" }}>
                <h2 style={{ fontSize: 20, fontWeight: 400, marginBottom: 4, textAlign: "center" }}>Let's Map Out Your Strategy</h2>
                <p style={{ fontSize: 13, ...O, color: h.muted, marginBottom: 24, textAlign: "center" }}>Your progress saves automatically. Come back anytime.</p>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12.5, ...O, color: h.muted, marginBottom: 4, display: "block" }}>Your first name</label>
                  <input data-testid="welcome-name" type="text" placeholder="e.g., Sandra" ref={nameRef} defaultValue="" tabIndex={1}
                    style={{ width: "100%", padding: "13px 16px", borderRadius: 8, border: `1.5px solid ${h.border}`, background: h.cream, fontSize: 15, ...O, color: h.text, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 12.5, ...O, color: h.muted, marginBottom: 4, display: "block" }}>Your email address</label>
                  <input data-testid="welcome-email" type="email" placeholder="you@email.com" ref={emailRef} defaultValue="" tabIndex={2}
                    onChange={() => setEmailErr("")}
                    onKeyDown={e => { if (e.key === "Enter") handleWelcome(); }}
                    style={{ width: "100%", padding: "13px 16px", borderRadius: 8, border: `1.5px solid ${emailErr ? "#c47a7a" : h.border}`, background: h.cream, fontSize: 15, ...O, color: h.text, outline: "none", boxSizing: "border-box" }} />
                  {emailErr && <p style={{ fontSize: 12, color: "#c47a7a", ...O, marginTop: 4 }}>{emailErr}</p>}
                </div>
                <Btn data-testid="welcome-submit" onClick={handleWelcome} sx={{ width: "100%" }}>Let's Get Started &rarr;</Btn>
                <p style={{ fontSize: 11, color: h.light, ...O, marginTop: 14, textAlign: "center" }}>No spam, ever. Your info is safe.</p>
              </div>
              <p style={{ fontSize: 12, color: h.light, ...O, marginTop: 16, textAlign: "center" }}>Created by Robyn Bennett, Certified Business Strategist</p>
            </div>
          </div>
        ) : (
          /* Mobile: stacked strategically */
          <div>
            <img src="/logo.png" alt="Robyn Bennett" style={{ height: 50, marginBottom: 14, opacity: 0.9 }} />
            <h1 style={{ fontSize: 28, fontWeight: 400, lineHeight: 1.2, marginBottom: 10 }}>
              The Business<br />Essentials Guide
            </h1>
            <p style={{ fontSize: 14, color: h.taupe, ...O, fontWeight: 500, marginBottom: 20 }}>For women over 50 who are ready to turn their experience into income.</p>

            {/* Form first on mobile */}
            <div style={{ background: h.white, borderRadius: 16, padding: "24px 22px", border: `1px solid ${h.border}`, boxShadow: "0 4px 24px rgba(0,0,0,.04)", marginBottom: 28 }}>
              <h2 style={{ fontSize: 18, fontWeight: 400, marginBottom: 4, textAlign: "center" }}>Let's Map Out Your Strategy</h2>
              <p style={{ fontSize: 12, ...O, color: h.muted, marginBottom: 20, textAlign: "center" }}>Your progress saves automatically. Come back anytime.</p>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12.5, ...O, color: h.muted, marginBottom: 4, display: "block" }}>Your first name</label>
                <input data-testid="welcome-name" type="text" placeholder="e.g., Sandra" ref={nameRef} defaultValue="" tabIndex={1}
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 8, border: `1.5px solid ${h.border}`, background: h.cream, fontSize: 15, ...O, color: h.text, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12.5, ...O, color: h.muted, marginBottom: 4, display: "block" }}>Your email address</label>
                <input data-testid="welcome-email" type="email" placeholder="you@email.com" ref={emailRef} defaultValue="" tabIndex={2}
                  onChange={() => setEmailErr("")}
                  onKeyDown={e => { if (e.key === "Enter") handleWelcome(); }}
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 8, border: `1.5px solid ${emailErr ? "#c47a7a" : h.border}`, background: h.cream, fontSize: 15, ...O, color: h.text, outline: "none", boxSizing: "border-box" }} />
                {emailErr && <p style={{ fontSize: 12, color: "#c47a7a", ...O, marginTop: 4 }}>{emailErr}</p>}
              </div>
              <Btn data-testid="welcome-submit" onClick={handleWelcome} sx={{ width: "100%" }}>Let's Get Started &rarr;</Btn>
              <p style={{ fontSize: 11, color: h.light, ...O, marginTop: 12, textAlign: "center" }}>No spam, ever. Your info is safe.</p>
            </div>

            {/* Bullet points after form on mobile */}
            <p style={{ fontSize: 14, color: h.muted, lineHeight: 1.65, ...O, marginBottom: 16 }}>
              Answer real questions about YOUR life and goals, get on-demand guidance, and walk away with a business strategy built around you.
            </p>
            <div style={{ marginBottom: 16 }}>
              {["Uncover the business strengths hiding in your experience", "Get a customized strategy mapping out your first offer and pricing", "A 90-day roadmap with specific action steps", "On-demand support that adapts to your exact situation"].map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                  <span style={{ color: h.taupe, fontSize: 13, marginTop: 1 }}>&#10003;</span>
                  <span style={{ fontSize: 13.5, ...O, color: h.text, lineHeight: 1.5 }}>{t}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 13, ...O, color: h.accent, fontStyle: "italic", lineHeight: 1.6, marginBottom: 8 }}>
              "I wish someone had given me this when I started. Clear, simple, and it actually understands where I'm coming from."
            </p>
            <p style={{ fontSize: 12, color: h.light, ...O, textAlign: "center", marginTop: 16 }}>Created by Robyn Bennett, Certified Business Strategist</p>
          </div>
        )}
      </div>
    </div>
  );

  const StageView = () => {
    const displayName = name && name !== "Friend" ? name.charAt(0).toUpperCase() + name.slice(1) : "";
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: `${padding}px` }}>
        <Wrapper>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <h2 style={{ fontSize: 22, fontWeight: 400, marginBottom: 6 }}>Quick question before we start{displayName ? `, ${displayName}` : ""}.</h2>
            <p style={{ fontSize: 14, ...O, color: h.muted }}>This helps me personalize everything for you.</p>
          </div>
          <p style={{ fontSize: 14, ...O, color: h.text, fontWeight: 500, marginBottom: 16 }}>Where are you right now with your business?</p>
          {STAGES.map(s => (
            <button key={s.id} data-testid={`stage-${s.id}`} onClick={() => handleStage(s.id)}
              style={{ display: "block", width: "100%", background: h.white, border: `1.5px solid ${h.border}`, borderRadius: 12, padding: "18px 22px", marginBottom: 10, cursor: "pointer", textAlign: "left" }}>
              <div style={{ fontSize: 16, ...O, fontWeight: 500, color: h.text, marginBottom: 2 }}>{s.label}</div>
              <div style={{ fontSize: 13, ...O, color: h.muted }}>{s.desc}</div>
            </button>
          ))}
        </Wrapper>
      </div>
    );
  };

  const DashboardView = () => {
    const displayName = name && name !== "Friend" ? name.charAt(0).toUpperCase() + name.slice(1) : "";
    const nextSecIdx = SECTIONS.findIndex((s, i) => secProgress(i).d < secProgress(i).t);
    return (
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto" }}>
        <ProgressBar pct={progressPct} />
        <Wrapper style={{ padding: `${isDesktop ? 40 : 24}px ${padding}px` }}>
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 13, ...O, color: h.taupeD, marginBottom: 4, fontStyle: "italic" }}>{affirmation}</div>
            <h1 style={{ fontSize: isDesktop ? 28 : 22, fontWeight: 400, lineHeight: 1.3, marginBottom: 4 }}>
              {displayName ? `${displayName}'s` : "Your"} Business Essentials
            </h1>
            <p style={{ fontSize: 14, ...O, color: h.muted }}>{progressPct}% complete {progressPct > 0 && progressPct < 100 ? "- keep going!" : ""}</p>
          </div>

          <p style={{ fontSize: 14, ...O, color: h.muted, lineHeight: 1.6, marginBottom: 20 }}>
            Work through each step in order. Each one builds on the last, so by the end you'll have a clear, personalized strategy.
          </p>

          {SECTIONS.map((sec, idx) => {
            const sp = secProgress(idx);
            const isComplete = sp.d === sp.t;
            const isNext = idx === (nextSecIdx >= 0 ? nextSecIdx : 0);
            return (
              <button key={sec.id} data-testid={`dashboard-section-${sec.id}`}
                onClick={() => { setSecIdx(idx); setLesIdx(0); setView("step"); }}
                style={{
                  display: "block", width: "100%",
                  background: isNext && !isComplete ? h.goldL : isComplete ? h.okL : h.white,
                  border: isNext && !isComplete ? `1.5px solid ${h.gold}` : isComplete ? `1.5px solid ${h.ok}` : `1px solid ${h.border}`,
                  borderRadius: 14, padding: "20px 22px", marginBottom: 12,
                  cursor: "pointer", textAlign: "left", opacity: 1
                }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: isComplete ? h.ok : isNext ? h.btnPrimary : h.border, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15, fontWeight: 600, ...O, flexShrink: 0 }}>
                      {isComplete ? "\u2713" : sec.id}
                    </div>
                    <div>
                      <div style={{ fontSize: 16, ...O, fontWeight: 500, color: h.text }}>Step {sec.id}. {sec.title}</div>
                      <div style={{ fontSize: 13, ...O, color: h.muted }}>{sec.sub}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 12, ...O, color: isComplete ? h.ok : h.light }}>{sp.d}/{sp.t}</div>
                    {isNext && !isComplete && <div style={{ fontSize: 11, ...O, color: h.btnPrimary, fontWeight: 500, marginTop: 2 }}>Up next</div>}
                  </div>
                </div>
              </button>
            );
          })}

          {/* Strategy Section - only show when all done or close to done */}
          {allFilled && !genPlan && !genLoading && (
            <div style={{ background: h.goldL, borderRadius: 14, padding: "24px", marginTop: 20, border: `1.5px solid ${h.gold}` }}>
              <div style={{ fontSize: 11, ...O, letterSpacing: ".15em", color: h.taupeD, textTransform: "uppercase", marginBottom: 8 }}>All steps complete</div>
              <p style={{ fontSize: 15, ...O, color: h.text, lineHeight: 1.6, marginBottom: 12 }}>
                You've done the work. Now let's turn your answers into a real strategy.
              </p>
              <Btn data-testid="gen-strategy-btn" onClick={generateStrategy}>Generate My Strategy &#10022;</Btn>
            </div>
          )}

          {!allFilled && !genPlan && !genLoading && (
            <p style={{ fontSize: 13, ...O, color: h.light, marginTop: 8, textAlign: "center" }}>
              Complete all 4 steps to unlock your personalized strategy.
            </p>
          )}

          {genLoading && (
            <div style={{ background: h.goldL, borderRadius: 14, padding: "28px", textAlign: "center", marginTop: 16 }}>
              <p style={{ fontSize: 16, ...O, color: h.text, marginBottom: 6 }}>Creating your personalized strategy...</p>
              <p style={{ fontSize: 13, ...O, color: h.muted }}>Analyzing your answers. This takes about 15 seconds.</p>
            </div>
          )}

          {genPlan && (
            <div style={{ marginTop: 16 }}>
              {/* Your Answers Summary */}
              <div style={{ background: h.white, borderRadius: 14, padding: "22px 26px", marginBottom: 16, border: `1px solid ${h.border}` }}>
                <div style={{ fontSize: 10.5, ...O, letterSpacing: ".15em", color: h.taupeD, textTransform: "uppercase", marginBottom: 12 }}>What you shared</div>
                {ans.v_draft && <p style={{ fontSize: 14, ...O, color: h.text, lineHeight: 1.55, marginBottom: 8 }}><strong>Your vision:</strong> {ans.v_draft}</p>}
                {ans.v_goals && <p style={{ fontSize: 14, ...O, color: h.text, lineHeight: 1.55, marginBottom: 8 }}><strong>Goals:</strong> {ans.v_goals}</p>}
                {ans.av_name && <p style={{ fontSize: 14, ...O, color: h.text, lineHeight: 1.55, marginBottom: 8 }}><strong>Ideal client:</strong> {ans.av_name}{ans.av_sitch ? ` - ${ans.av_sitch}` : ""}</p>}
                {ans.g30 && <p style={{ fontSize: 14, ...O, color: h.text, lineHeight: 1.55, marginBottom: 0 }}><strong>30-day goal:</strong> {ans.g30}</p>}
              </div>

              <div style={{ fontSize: 10.5, ...O, letterSpacing: ".15em", color: h.taupeD, textTransform: "uppercase", marginBottom: 12 }}>Your personalized strategy</div>
              {[
                { key: "YOUR UNFAIR ADVANTAGE", title: "Your Unfair Advantage", color: h.goldL, borderColor: h.gold },
                { key: "RECOMMENDED FIRST OFFER", title: "Recommended First Offer", color: h.okL, borderColor: h.ok },
                { key: "CLIENT ATTRACTION STRATEGY", title: "Client Attraction Strategy", color: h.taupeL, borderColor: h.taupe },
                { key: "BIGGEST RISK TO WATCH", title: "Biggest Risk to Watch", color: "#FFF5F0", borderColor: "#E8C4B0" }
              ].map(({ key, title, color, borderColor }) => genPlan[key] && (
                <div key={key} style={{ background: color, borderRadius: 14, padding: "22px 26px", marginBottom: 12, borderLeft: `3px solid ${borderColor}` }}>
                  <div style={{ fontSize: 10.5, ...O, letterSpacing: ".15em", color: h.taupeD, textTransform: "uppercase", marginBottom: 8 }}>{title}</div>
                  <p style={{ fontSize: 15, ...O, lineHeight: 1.65, margin: 0 }}>{genPlan[key]}</p>
                </div>
              ))}
              <div style={{ textAlign: "center", marginBottom: 18 }}>
                <Btn v="s" data-testid="regen-btn" onClick={() => { setGenPlan(null); setTimeout(generateStrategy, 100); }} sx={{ fontSize: 13, padding: "10px 20px" }}>Regenerate Strategy</Btn>
              </div>
            </div>
          )}

          {/* CTA - moved to bottom, more subtle */}
          {(progressPct >= 50 || genPlan) && (
            <div style={{ background: `linear-gradient(135deg,${h.taupe},${h.taupeD})`, borderRadius: 14, padding: "24px", textAlign: "center", marginTop: 24, color: "#fff" }}>
              <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 8, ...O }}>Want hands-on help bringing this to life?</p>
              <a href="https://robynbennett.co/schedule" target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-block", background: "#fff", color: h.text, borderRadius: 8, padding: "12px 24px", fontSize: 15, fontWeight: 500, textDecoration: "none", ...O }}>
                Book a Call with Robyn &rarr;
              </a>
            </div>
          )}

          {/* Reset */}
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button data-testid="reset-btn" onClick={resetAll}
              style={{ background: "none", border: "none", color: h.light, fontSize: 12, cursor: "pointer", ...O }}>
              Start Over
            </button>
          </div>
        </Wrapper>
      </div>
    );
  };

  const STEP_INTROS = {
    1: "Before we build anything, let's make sure your mindset is set for success. This step helps you name the fears holding you back and replace them with what's actually true.",
    2: "You've lived a full life — and that's your biggest business asset. In this step, you'll map the experiences that make you uniquely qualified to help your future clients.",
    3: "Now let's get clear on where you're going. You'll craft your vision, set real goals, and map out your first 90 days with specific action steps.",
    4: "The clearer you are about WHO you serve, the easier everything else becomes. This step helps you define your ideal client and how to show up for her."
  };

  const StepView = () => {
    const sec = SECTIONS[secIdx];
    return (
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto" }}>
        <ProgressBar pct={progressPct} />
        <Wrapper style={{ padding: `${isDesktop ? 40 : 24}px ${padding}px` }}>
          <BackBtn onClick={() => setView("dashboard")} label="Back to all steps" />
          <div style={{ fontSize: 11, ...O, letterSpacing: ".15em", color: h.taupeD, textTransform: "uppercase", marginBottom: 6 }}>Step {sec.id} of {SECTIONS.length}</div>
          <h1 style={{ fontSize: isDesktop ? 28 : 22, fontWeight: 400, lineHeight: 1.3, marginBottom: 8 }}>{sec.title}</h1>
          <p style={{ fontSize: 15, ...O, color: h.muted, lineHeight: 1.65, marginBottom: 24 }}>{STEP_INTROS[sec.id]}</p>

          <p style={{ fontSize: 13, ...O, color: h.taupeD, fontWeight: 500, marginBottom: 12 }}>Here's what's inside this step:</p>

          {sec.lessons.map((les, idx) => {
            const isDone = done.includes(les.id);
            const typeLabels = { learn: "Read & Learn", activity: "Your Turn - Fill In", reflect: "Pause & Reflect" };
            return (
              <button key={les.id} data-testid={`lesson-btn-${les.id}`}
                onClick={() => { setLesIdx(idx); setView("lesson"); setWarning(""); }}
                style={{
                  display: "flex", width: "100%", alignItems: "center", gap: 14,
                  background: isDone ? h.okL : h.white,
                  border: `1px solid ${isDone ? h.ok : h.border}`,
                  borderRadius: 12, padding: "16px 18px", marginBottom: 10,
                  cursor: "pointer", textAlign: "left"
                }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", border: `2px solid ${isDone ? h.ok : h.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: isDone ? h.ok : h.light, flexShrink: 0 }}>
                  {isDone ? "\u2713" : `${sec.id}.${idx + 1}`}
                </div>
                <div>
                  <div style={{ fontSize: 14, ...O, fontWeight: 500, color: h.text }}>{les.title}</div>
                  <div style={{ fontSize: 12, ...O, color: h.muted }}>{typeLabels[les.type]}</div>
                </div>
              </button>
            );
          })}

          <Btn data-testid="start-first-lesson" onClick={() => { setLesIdx(0); setView("lesson"); setWarning(""); }} sx={{ marginTop: 16, width: isDesktop ? "auto" : "100%" }}>
            Begin Step {sec.id} &rarr;
          </Btn>
        </Wrapper>
      </div>
    );
  };

  const LessonView = () => {
    const sec = SECTIONS[secIdx];
    const les = sec.lessons[lesIdx];
    const isLast = secIdx === SECTIONS.length - 1 && lesIdx === sec.lessons.length - 1;

    return (
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto" }}>
        <ProgressBar pct={progressPct} />
        <Wrapper style={{ padding: `${isDesktop ? 40 : 24}px ${padding}px` }}>
          <BackBtn onClick={() => { setView("step"); setWarning(""); }} label={`Step ${sec.id}: ${sec.title}`} />
          <div style={{ fontSize: 11, ...O, letterSpacing: ".15em", color: h.taupeD, textTransform: "uppercase", marginBottom: 8 }}>
            {{ learn: "\u25C7 Learn", activity: "\u270E Activity", reflect: "\u25CC Reflect" }[les.type]}
          </div>
          <h1 style={{ fontSize: isDesktop ? 26 : 20, fontWeight: 400, lineHeight: 1.3, marginBottom: 20 }}>{les.title}</h1>

          {/* Learn */}
          {les.type === "learn" && (
            <>
              {personalizing && !personalized[les.id] && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: h.goldL, borderRadius: 10, marginBottom: 20 }}>
                  <span>&#10022;</span>
                  <p style={{ fontSize: 13.5, ...O, color: h.taupeD, margin: 0 }}>Personalizing for you...</p>
                </div>
              )}
              {personalized[les.id] && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", background: h.okL, borderRadius: 10, marginBottom: 20 }}>
                  <span style={{ color: h.ok }}>&#10003;</span>
                  <p style={{ fontSize: 12.5, ...O, color: h.ok, margin: 0 }}>Personalized based on your answers</p>
                </div>
              )}
              {(personalized[les.id]?.content || les.content).map((p, i) => (
                <p key={i} style={{ fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>{p}</p>
              ))}
              <div style={{ background: h.goldL, borderRadius: 12, padding: "18px 22px", marginTop: 8, marginBottom: 24, borderLeft: `3px solid ${h.gold}` }}>
                <div style={{ fontSize: 10.5, ...O, letterSpacing: ".1em", color: h.taupeD, textTransform: "uppercase", marginBottom: 6 }}>Key Takeaway</div>
                <p style={{ fontSize: 15, fontStyle: "italic", color: h.text, margin: 0, lineHeight: 1.6 }}>{personalized[les.id]?.takeaway || les.takeaway}</p>
              </div>
            </>
          )}

          {/* Activity - CONTROLLED textareas */}
          {les.type === "activity" && (
            <>
              <p style={{ fontSize: 15, ...O, color: h.muted, lineHeight: 1.65, marginBottom: 24 }}>{les.intro}</p>
              {les.fields.map((f) => (
                <TextArea key={f.id} id={f.id} label={f.label} ph={f.ph} val={ans[f.id]} onChange={updateAns} />
              ))}
            </>
          )}

          {/* Reflect - CONTROLLED textarea */}
          {les.type === "reflect" && (
            <>
              <div style={{ background: `linear-gradient(135deg,${h.taupe},${h.taupeD})`, borderRadius: 14, padding: "22px 26px", color: "#fff", marginBottom: 24 }}>
                <p style={{ fontSize: 16, lineHeight: 1.65, fontStyle: "italic", margin: 0 }}>{les.prompt}</p>
              </div>
              <textarea
                data-testid={`textarea-${les.fieldId}`}
                value={ans[les.fieldId] || ""}
                onChange={e => updateAns(les.fieldId, e.target.value)}
                placeholder="Write freely here..."
                rows={8}
                style={{
                  width: "100%", padding: "16px 18px", borderRadius: 12,
                  border: `1.5px solid ${h.border}`, background: h.white,
                  fontSize: 16, fontFamily: "Georgia,serif", color: h.text,
                  outline: "none", resize: "vertical", lineHeight: 1.75,
                  boxSizing: "border-box"
                }}
              />
            </>
          )}

          {/* Continue / Warning */}
          <div style={{ marginTop: 24 }}>
            {warning && (
              <div style={{ background: "#FFF5F0", border: "1px solid #E8C4B0", borderRadius: 10, padding: "14px 18px", marginBottom: 16 }}>
                <p style={{ fontSize: 14, ...O, color: "#8B5E3C", margin: 0, lineHeight: 1.55 }}>{warning}</p>
              </div>
            )}
            <Btn data-testid="continue-btn" onClick={handleContinue} sx={{ width: isDesktop ? "auto" : "100%", minWidth: 240 }}>
              {isLast ? "Complete & View My Plan \u2726" : "Continue \u2192"}
            </Btn>
            {les.type !== "learn" && <p style={{ fontSize: 12, ...O, color: h.light, marginTop: 10 }}>Your answers save automatically</p>}
          </div>
        </Wrapper>
      </div>
    );
  };

  const SummaryView = () => {
    const answerSummary = [
      { label: "Your vision", value: ans.v_draft },
      { label: "Goals", value: ans.v_goals },
      { label: "Who you serve", value: ans.v_who },
      { label: "Impact you want to make", value: ans.v_impact },
      { label: "Ideal client", value: ans.av_name ? `${ans.av_name}${ans.av_age ? ` (${ans.av_age})` : ""} - ${ans.av_sitch || ""}` : null },
      { label: "Client's struggle", value: ans.av_pain },
      { label: "Client's dream", value: ans.av_dream },
      { label: "30-day goal", value: ans.g30 },
      { label: "60-day goal", value: ans.g60 },
      { label: "90-day goal", value: ans.g90 },
    ].filter(a => a.value && a.value.trim());

    return (
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto" }}>
        <ProgressBar pct={progressPct} />
        <Wrapper style={{ padding: `${isDesktop ? 40 : 24}px ${padding}px` }}>
          <BackBtn onClick={() => setView("dashboard")} label="Dashboard" />
          <h1 style={{ fontSize: isDesktop ? 28 : 22, fontWeight: 400, lineHeight: 1.3, marginBottom: 6 }}>Your Personalized Strategy</h1>
          <p style={{ fontSize: 14, ...O, color: h.muted, marginBottom: 24 }}>Built from everything you shared across all 4 steps.</p>

          {/* What You Shared */}
          {answerSummary.length > 0 && (
            <div style={{ background: h.white, borderRadius: 14, padding: "22px 26px", marginBottom: 24, border: `1px solid ${h.border}` }}>
              <div style={{ fontSize: 10.5, ...O, letterSpacing: ".15em", color: h.taupeD, textTransform: "uppercase", marginBottom: 14 }}>What you shared</div>
              {answerSummary.map((a, i) => (
                <p key={i} style={{ fontSize: 14, ...O, color: h.text, lineHeight: 1.55, marginBottom: i < answerSummary.length - 1 ? 8 : 0 }}>
                  <span style={{ color: h.muted }}>{a.label}:</span> {a.value}
                </p>
              ))}
            </div>
          )}

          {!allFilled && !genPlan && !genLoading && (
            <>
              <p style={{ fontSize: 15, ...O, color: h.muted, lineHeight: 1.6, marginBottom: 20 }}>
                You still have some steps to complete. Go back and finish them to get your full personalized strategy.
              </p>
              <Btn onClick={() => { setSecIdx(0); setLesIdx(0); setView("step"); }}>Complete the Steps &rarr;</Btn>
            </>
          )}

          {genLoading && (
            <div style={{ background: h.goldL, borderRadius: 14, padding: "28px", textAlign: "center" }}>
              <p style={{ fontSize: 16, ...O, color: h.text, marginBottom: 6 }}>Creating your personalized strategy...</p>
              <p style={{ fontSize: 13, ...O, color: h.muted }}>Analyzing your answers. This takes about 15 seconds.</p>
            </div>
          )}

          {genPlan && (
            <>
              <div style={{ fontSize: 10.5, ...O, letterSpacing: ".15em", color: h.taupeD, textTransform: "uppercase", marginBottom: 12 }}>Your strategy</div>
              {[
                { key: "YOUR UNFAIR ADVANTAGE", title: "Your Unfair Advantage", color: h.goldL, borderColor: h.gold },
                { key: "RECOMMENDED FIRST OFFER", title: "Recommended First Offer", color: h.okL, borderColor: h.ok },
                { key: "CLIENT ATTRACTION STRATEGY", title: "Client Attraction Strategy", color: h.taupeL, borderColor: h.taupe },
                { key: "BIGGEST RISK TO WATCH", title: "Biggest Risk to Watch", color: "#FFF5F0", borderColor: "#E8C4B0" }
              ].map(({ key, title, color, borderColor }) => genPlan[key] && (
                <div key={key} style={{ background: color, borderRadius: 14, padding: "22px 26px", marginBottom: 12, borderLeft: `3px solid ${borderColor}` }}>
                  <div style={{ fontSize: 10.5, ...O, letterSpacing: ".15em", color: h.taupeD, textTransform: "uppercase", marginBottom: 8 }}>{title}</div>
                  <p style={{ fontSize: 15, ...O, lineHeight: 1.65, margin: 0 }}>{genPlan[key]}</p>
                </div>
              ))}
              <div style={{ textAlign: "center", marginTop: 12, marginBottom: 24 }}>
                <Btn v="s" onClick={() => { setGenPlan(null); setTimeout(generateStrategy, 100); }}>Regenerate Strategy</Btn>
              </div>
            </>
          )}

          <div style={{ background: `linear-gradient(135deg,${h.taupe},${h.taupeD})`, borderRadius: 14, padding: "28px", textAlign: "center", marginTop: 20, color: "#fff" }}>
            <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 8, ...O }}>Ready to bring this strategy to life?</p>
            <a href="https://robynbennett.co/schedule" target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-block", background: "#fff", color: h.text, borderRadius: 8, padding: "12px 24px", fontSize: 15, fontWeight: 500, textDecoration: "none", ...O }}>
              Book a Call with Robyn &rarr;
            </a>
          </div>
        </Wrapper>
      </div>
    );
  };

  /* ─── Coach Chat Panel ─── */
  const CoachPanel = () => {
    if (view === "welcome" || view === "stage") return null;

    if (!chatOpen) {
      return (
        <button data-testid="coach-fab" onClick={() => setChatOpen(true)}
          style={{
            position: "fixed", bottom: 80, right: 16,
            borderRadius: 28, background: `linear-gradient(135deg,${h.accent},${h.taupeD})`,
            border: "none", color: "#fff", cursor: "pointer",
            boxShadow: "0 4px 16px rgba(0,0,0,.18)", display: "flex",
            alignItems: "center", gap: 8, zIndex: 99,
            padding: "14px 20px", ...O
          }}>
          <span style={{ fontSize: 18 }}>&#10023;</span>
          <span style={{ fontSize: 14, fontWeight: 500, whiteSpace: "nowrap" }}>Ask Robyn AI</span>
        </button>
      );
    }

    return (
      <div style={{
        position: "fixed", bottom: 0, right: 0,
        width: isDesktop ? 400 : "100%", height: isDesktop ? 520 : "85vh",
        background: h.cream, borderRadius: "16px 16px 0 0",
        boxShadow: "0 -4px 32px rgba(0,0,0,.12)", zIndex: 100,
        display: "flex", flexDirection: "column", border: `1px solid ${h.border}`
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: `1px solid ${h.border}`, background: h.white, borderRadius: "16px 16px 0 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg,${h.accent},${h.taupeD})`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>&#10023;</div>
            <div>
              <div style={{ fontSize: 14, ...O, fontWeight: 600 }}>Robyn AI</div>
              <div style={{ fontSize: 11, ...O, color: h.light }}>{MAX_COACH_PER_DAY - coachCount} questions left today</div>
            </div>
          </div>
          <button data-testid="close-coach" onClick={() => setChatOpen(false)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: h.muted, padding: "4px 8px" }}>&times;</button>
        </div>

        {/* Messages */}
        <div ref={chatScrollRef} style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
          {chatMsgs.length === 0 && (
            <div>
              <p style={{ fontSize: 13.5, ...O, color: h.muted, lineHeight: 1.6, marginBottom: 14 }}>Ask me anything about your business.</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {["What should my first offer be?", "How do I find clients?", "What AI tools should I use?", "How do I price my services?"].map(q => (
                  <button key={q} data-testid={`coach-suggestion-${q.slice(0,10)}`} onClick={() => sendChat(q)}
                    style={{ background: h.goldL, border: `1px solid ${h.gold}`, borderRadius: 20, padding: "8px 14px", fontSize: 12.5, ...O, color: h.text, cursor: "pointer" }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {chatMsgs.map((m, i) => (
            <div key={i} style={{ marginBottom: 12, display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{
                background: m.role === "user" ? h.btnPrimary : h.white,
                color: m.role === "user" ? "#fff" : h.text,
                borderRadius: 14, padding: "12px 16px", maxWidth: "85%",
                fontSize: 14, lineHeight: 1.6, ...O,
                border: m.role === "user" ? "none" : `1px solid ${h.border}`
              }}>
                {m.text}
              </div>
            </div>
          ))}
          {chatLoading && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ background: h.white, borderRadius: 14, padding: "12px 16px", display: "inline-block", border: `1px solid ${h.border}` }}>
                <span style={{ fontSize: 14, color: h.muted, ...O }}>Thinking...</span>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{ padding: "12px 16px 20px", borderTop: `1px solid ${h.border}`, display: "flex", gap: 8 }}>
          <input
            data-testid="coach-input"
            type="text"
            value={coachInput}
            onChange={e => setCoachInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && coachInput.trim()) { sendChat(coachInput); setCoachInput(""); } }}
            placeholder="Ask anything..."
            style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: `1px solid ${h.border}`, fontSize: 14, ...O, outline: "none", background: h.white }}
          />
          <button data-testid="coach-send" onClick={() => { if (coachInput.trim()) { sendChat(coachInput); setCoachInput(""); } }}
            style={{ background: h.btnPrimary, color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 14, cursor: "pointer", position: "relative", zIndex: 101 }}>
            &rarr;
          </button>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="app-root" style={{ minHeight: "100vh", background: h.cream, color: h.text, ...O }}>
      <style>{`
        ::placeholder { color: ${h.light}; }
        textarea:focus, input:focus { border-color: ${h.taupe} !important; }
        button:hover { opacity: 0.9; }
      `}</style>
      {view === "welcome" && WelcomeView()}
      {view === "stage" && StageView()}
      {view === "dashboard" && DashboardView()}
      {view === "step" && StepView()}
      {view === "lesson" && LessonView()}
      {view === "summary" && SummaryView()}
      {CoachPanel()}
      <footer style={{ textAlign: "center", padding: "20px", fontSize: 12, color: h.light, ...O }}>
        &copy; {new Date().getFullYear()} Robyn Bennett. All rights reserved.
      </footer>
    </div>
  );
}

export default App;
